import React, {useEffect, useMemo, useRef} from "react";
import useVideo from "../../../../services/stores/VideoStore";
import PropTypes from "prop-types";
import Helpers from "../../../../services/Helpers";
import {Collapse} from "bootstrap";
import MarkdownViewer from "../../../components/MarkdownViewer";
import Player from "@vimeo/player";
import {
  clearPlaybackProgress,
  isEffectivelyComplete,
  normalizePlaybackProgress,
  readPlaybackProgress,
  savePlaybackProgress,
} from "../playbackProgress";

export const PLAYBACK_SAVE_INTERVAL_MS = 5000;

function ignoreRejected(result) {
  if (result && typeof result.catch === "function") {
    result.catch(() => {});
  }
}

export default function Video(props) {
  const {assetUrl, isNotEmpty} = Helpers();
  const {video} = useVideo(props.token, props.id, props.active);
  const iframeRef = useRef(null);
  const restoreProgress = useMemo(() => {
    if (!props.active || [null, undefined, ""].includes(video.vimeoId)) {
      return null;
    }

    return readPlaybackProgress({
      memberId: props.memberId,
      playlistId: props.playlistId,
      videoId: props.id,
      vimeoId: video.vimeoId,
    });
  }, [props.active, props.id, props.memberId, props.playlistId, video.vimeoId]);

  useEffect(() => {
    video.files.find(f => f.contentType.indexOf("audio/") !== -1)
    props.updateDownloads(video.files.filter(f => f.contentType.indexOf("audio/") === -1));
  }, [video]);

  useEffect(() => {
    const element = document.getElementById(`v-${props.id}`);
    const collapse = new Collapse(element, {toggle: false});
    props.active ? collapse.show() : collapse.hide();
    if (!props.active) {
      props.updateDownloads([]);
    }
  }, [props.active, props.id]);

  useEffect(() => {
    if (!props.active || [null, undefined, ""].includes(video.vimeoId) || iframeRef.current === null) {
      return undefined;
    }

    const identity = {
      memberId: props.memberId,
      playlistId: props.playlistId,
      videoId: props.id,
      vimeoId: video.vimeoId,
    };
    let player;
    let latestProgress = null;
    let lastSavedAt = null;
    const registeredListeners = [];

    const persist = (data, force = false) => {
      const progress = normalizePlaybackProgress(data);
      if (progress === null) {
        return;
      }

      if (isEffectivelyComplete(progress)) {
        clearPlaybackProgress(identity);
        latestProgress = null;
        lastSavedAt = null;
        return;
      }

      latestProgress = progress;
      const now = Date.now();
      if (force || lastSavedAt === null || now - lastSavedAt >= PLAYBACK_SAVE_INTERVAL_MS) {
        savePlaybackProgress(identity, progress);
        lastSavedAt = now;
      }
    };

    const flush = () => {
      if (latestProgress !== null) {
        savePlaybackProgress(identity, latestProgress);
        lastSavedAt = Date.now();
      }
    };

    const handleTimeUpdate = data => persist(data);
    const handlePause = data => {
      const progress = normalizePlaybackProgress(data);
      if (progress === null) {
        flush();
      } else {
        persist(progress, true);
      }
    };
    const handleEnded = () => {
      clearPlaybackProgress(identity);
      latestProgress = null;
      lastSavedAt = null;
    };
    const handlePageHide = () => flush();

    try {
      player = new Player(iframeRef.current);
    } catch (error) {
      return undefined;
    }

    [
      ["timeupdate", handleTimeUpdate],
      ["pause", handlePause],
      ["ended", handleEnded],
    ].forEach(([event, handler]) => {
      try {
        ignoreRejected(player.on(event, handler));
        registeredListeners.push([event, handler]);
      } catch (error) {
        // The iframe remains usable without SDK event handling.
      }
    });

    try {
      ignoreRejected(player.ready());
    } catch (error) {
      // The iframe remains usable when Vimeo readiness fails.
    }

    let pageHideRegistered = false;
    try {
      window.addEventListener("pagehide", handlePageHide);
      pageHideRegistered = true;
    } catch (error) {
      // Browser lifecycle flushing is best-effort.
    }

    return () => {
      flush();
      if (pageHideRegistered) {
        try {
          window.removeEventListener("pagehide", handlePageHide);
        } catch (error) {
          // Browser lifecycle cleanup is best-effort.
        }
      }
      registeredListeners.forEach(([event, handler]) => {
        try {
          ignoreRejected(player.off(event, handler));
        } catch (error) {
          // Continue cleaning up the remaining listeners.
        }
      });
      try {
        ignoreRejected(player.destroy());
      } catch (error) {
        // React still removes the iframe when SDK cleanup fails.
      }
    };
  }, [props.active, props.id, props.memberId, props.playlistId, video.vimeoId]);

  function iframe() {
    let result = null;
    if (props.active && isNotEmpty(video.vimeoId)) {
      const startTime = restoreProgress !== null && restoreProgress.seconds > 0
        ? `#t=${Math.floor(restoreProgress.seconds)}s`
        : "";
      result =
        <div className="ratio ratio-16x9">
          <iframe
            ref={iframeRef}
            src={`https://player.vimeo.com/video/${video.vimeoId}${startTime}`}
            frameBorder="0"
            title="Meal Plans Classroom Tutorial"
            allowFullScreen=""
            allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
          />
        </div>

    }
    return result;
  }

  const audioFile = video.files.find(f => f.contentType.indexOf("audio/") !== -1);

  const audio = isNotEmpty(audioFile) ? (
    <div>
      <p>Listen to audio</p>
      <audio controls={true}>
        <source
          src={assetUrl(audioFile.storageKey)}
          type="audio/mpeg"
        />
      </audio>
    </div>
  ) : null;

  return props.active ? (
    <div id={`video-${props.id}`} className="accordion-item">
      <h2 className="accordion-header">
        <button
          className="accordion-button"
          type="button"
          onClick={() => props.setActive(null)}
        >
          <h3 className="mt-1 h-playlist">{props.title}</h3>
        </button>
      </h2>
      <div id={`v-${props.id}`} className="accordion-collapse collapse">
        <div className="accordion-body accordion-video">
          <div className="video-wrap">{iframe()}</div>
          <div className="mt-4 mb-3"><MarkdownViewer markdown={video.text} /></div>
          <div className="text-center">{audio}</div>
        </div>
      </div>
    </div>
  ) : (
    <div id={`video-${props.id}`}  className="accordion-item">
      <h2 className="accordion-header">
        <button
          className="accordion-button collapsed"
          type="button"
          onClick={props.setActive}
        >
          <h4 className="mt-1 h-playlist">{props.title}</h4>
        </button>
      </h2>
      <div id={`v-${props.id}`} className="accordion-collapse collapse">
        <div className="accordion-body">
        </div>
      </div>
    </div>
  );

}

Video.propTypes = {
  token: PropTypes.string,
  memberId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  playlistId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  id: PropTypes.number,
  title: PropTypes.string,
  active: PropTypes.bool,
  setActive: PropTypes.func,
  updateDownloads: PropTypes.func,
}
